/**
 * Agent Pages -- Activity
 *
 * Recent conversation history filtered for the current chatbot.
 */

import React, { useState, useEffect } from 'react'
import { useAgentContext } from '../../../components/layout/AgentContextLayout'
import { Card, CardContent } from '../../../components/ui/Card'
import { MessageCircle, Loader2, Clock, User, Inbox } from 'lucide-react'
import { api } from '../../../services/api'

interface ConversationSummary {
  conversation_id: string
  visitor_name?: string
  visitor_id?: string
  last_message?: string
  status: string
  created_at: string
  total_messages?: number
}

export const ActivityPage: React.FC = () => {
  const { chatbot } = useAgentContext()
  const [conversations, setConversations] = useState<ConversationSummary[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    const fetchConversations = async () => {
      setIsLoading(true)
      setError(null)
      try {
        const response = await api.get('/live-chat/conversations', {
          params: { chatbot_id: chatbot.id, limit: 50 },
        })
        if (!cancelled) {
          const data = response.data
          // Endpoint may return { conversations: [...] } or an array directly
          setConversations(Array.isArray(data) ? data : data.conversations ?? [])
        }
      } catch (err: unknown) {
        if (!cancelled) {
          const e = err as { response?: { data?: { detail?: string } } }
          setError(e?.response?.data?.detail ?? 'Failed to load conversations')
        }
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }
    fetchConversations()
    return () => { cancelled = true }
  }, [chatbot.id])

  // Format relative time
  const timeAgo = (iso: string) => {
    const diff = Date.now() - new Date(iso).getTime()
    const mins = Math.floor(diff / 60000)
    if (mins < 1) return 'Just now'
    if (mins < 60) return `${mins}m ago`
    const hrs = Math.floor(mins / 60)
    if (hrs < 24) return `${hrs}h ago`
    const days = Math.floor(hrs / 24)
    return `${days}d ago`
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold tracking-tight text-foreground">Activity</h1>

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      )}

      {/* Error */}
      {!isLoading && error && (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-sm text-destructive">{error}</p>
          </CardContent>
        </Card>
      )}

      {/* Empty state */}
      {!isLoading && !error && conversations.length === 0 && (
        <Card>
          <CardContent className="py-16 text-center space-y-3">
            <div className="mx-auto w-14 h-14 rounded-full bg-muted flex items-center justify-center">
              <Inbox className="h-7 w-7 text-muted-foreground" />
            </div>
            <p className="text-sm font-medium text-foreground">No conversations yet</p>
            <p className="text-xs text-muted-foreground max-w-xs mx-auto">
              Conversations will appear here once end users start chatting with this agent.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Conversation list */}
      {!isLoading && !error && conversations.length > 0 && (
        <div className="space-y-2">
          {conversations.map((conv) => (
            <Card key={conv.conversation_id} className="hover:bg-accent/50 transition-colors">
              <CardContent className="flex items-center gap-4 py-3 px-4">
                {/* Avatar */}
                <div className="flex-shrink-0 w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="h-4 w-4 text-primary" />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-foreground truncate">
                      {conv.visitor_name || conv.visitor_id || 'End User'}
                    </span>
                    <span
                      className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-xs font-medium ${
                        conv.status === 'active'
                          ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                          : 'bg-muted text-muted-foreground'
                      }`}
                    >
                      {conv.status}
                    </span>
                  </div>
                  {conv.last_message && (
                    <p className="text-xs text-muted-foreground truncate mt-0.5">
                      {conv.last_message}
                    </p>
                  )}
                </div>

                {/* Meta */}
                <div className="flex-shrink-0 flex flex-col items-end gap-1">
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {timeAgo(conv.created_at)}
                  </span>
                  {conv.total_messages != null && (
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      <MessageCircle className="h-3 w-3" />
                      {conv.total_messages}
                    </span>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
